import { FC, useState } from 'react';
import { navigate } from 'gatsby-link';
import { FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { merge as _merge } from 'lodash';
import { ButtonPrimary, Form, InputCheckbox, InputPhone } from '@lambdacurry/component-library';
import { FinishOnboardingInput } from 'lib/home/generated/types/api';
import { addressValidation, nameValidation } from 'lib/forms/validation';
import { addressInitialValues, nameInitialValues } from 'lib/forms/initialValues';
import { useFinishOnboardingMutation, useMeQuery } from 'lib/home/generated/queries';
import { useStripeConfirmSetupIntent } from '../../../hooks';
import { AddressInputGroup, NameInputGroup } from '../../InputGroups';
import { StripePaymentElement } from '../StripeForm';
import { submitHubspotContactToForm } from './CheckoutForm.helpers';
import { InputGrid } from '../../InputGrid';
import { CheckoutFormShippingAddressInputGroup } from './CheckoutFormShippingAddressInputGroup';

const initialValues = {
  ...nameInitialValues,
  phone: '',
  postal_code: '',
  shipping_address: addressInitialValues,
  billing_address: addressInitialValues,
  same_as_shipping: true
};

const validationSchema = Yup.object({
  ...nameValidation,
  shipping_address: Yup.object(addressValidation),
  same_as_shipping: Yup.boolean(),
  billing_address: Yup.object().when('same_as_shipping', {
    is: false,
    then: Yup.object(addressValidation)
  })
});

export type CheckoutFormValues = typeof initialValues;

export interface CheckoutFormProps {
  redirectPath: string;
  persistFunction: (values: CheckoutFormValues) => void;
  initialValues?: Partial<CheckoutFormValues>;
}

export const CheckoutForm: FC<CheckoutFormProps> = ({ redirectPath, initialValues: overrideInitialValues }) => {
  const { data: meData } = useMeQuery(undefined, {
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });
  const { confirm } = useStripeConfirmSetupIntent({ redirectPath });
  const finishOnboarding = useFinishOnboardingMutation();
  const [stripeStatus, setStripeStatus] = useState<'loading' | 'ready'>('loading');
  const { me } = meData || {};

  const handleSubmit = (values: CheckoutFormValues, formikHelpers: FormikHelpers<CheckoutFormValues>) => {
    confirm(
      {
        first_name: values.first_name,
        last_name: values.last_name,
        email: me?.email,
        phone: values.phone,
        ...(values.same_as_shipping ? values.shipping_address : values.billing_address),
        is_default: true
      },
      {
        onSuccess: () => {
          const finishOnboardingInput: FinishOnboardingInput = {
            first_name: values.first_name,
            last_name: values.last_name,
            phone: values.phone,
            email: me?.email,
            postal_code: values.shipping_address.postal_code,
            shipping_address: {
              ...values.shipping_address,
              first_name: values.first_name,
              last_name: values.last_name
            }
          };

          finishOnboarding.mutate(
            { finishOnboardingInput },
            {
              onSuccess: data => {
                const { id, user_id } = data.finishOnboarding;
                window.dataLayer.push({ event: 'finish_onboarding', subscription_id: id, user_id });

                submitHubspotContactToForm({
                  firstname: values.first_name,
                  lastname: values.last_name,
                  email: me.email,
                  phone: values.phone
                });
                navigate(redirectPath);
              },
              onError: () => formikHelpers.setSubmitting(false)
            }
          );
        },
        onError: () => formikHelpers.setSubmitting(false)
      }
    );
  };

  const mergedInitialValues = _merge(initialValues, overrideInitialValues);

  return (
    <Form<CheckoutFormValues>
      initialValues={mergedInitialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ isSubmitting, values }) => {
        return (
          <>
            <h4 className="h5">Account</h4>
            <InputGrid>
              <NameInputGroup />
              <InputPhone name="phone" label="Phone Number (optional)" />
            </InputGrid>

            <h4 className="h5">Shipping</h4>
            <CheckoutFormShippingAddressInputGroup />

            <h4 className="h5">Billing</h4>
            <InputCheckbox name="same_as_shipping" label="Same as shipping address" />

            {!values.same_as_shipping && <AddressInputGroup name="billing_address" />}

            {stripeStatus === 'loading' && <div className="pt-10">Loading Stripe payment form...</div>}

            <h4 className="h5">Payment</h4>
            <StripePaymentElement onReady={() => setStripeStatus('ready')} />

            <div className="mt-6">
              <ButtonPrimary
                className="w-full md:w-auto"
                data-cy="checkout-form-submit"
                disabled={stripeStatus !== 'ready' || isSubmitting}
                type="submit"
              >
                {isSubmitting ? <>Finishing...</> : <>Finish Registration</>}
              </ButtonPrimary>
            </div>
          </>
        );
      }}
    </Form>
  );
};
